/**
 * webm-to-mp4.js — «Сделать MP4» for a recorder whose take came out WebM.
 *
 * Where WebCodecs has no H.264 encoder the take is VP9/Opus in WebM: Yandex
 * Browser on PC (24 % of its screen takes, 30 d to 2026-10-20), part of Chrome
 * (13 %), Firefox always. Many places a person sends a screen recording to do
 * not take WebM, while both recorder pages promised «MP4». The only encoder
 * such a browser has is libx264 in FFmpeg WASM — this module is the ONE door
 * to it for /webcam-recorder (where the recipe was born, 2026-09-04) and
 * /screen-recorder (2026-10-20). Two copies of the recipe would drift.
 *
 *   WebmToMp4.convert(blob, { name, onProgress(frac), onLoad(frac), signal })
 *       → Promise<Blob> (video/mp4)
 *     throws err.code = 'too-big'        — FFmpegMemoryGuard refused the file
 *                       'ffmpeg-unavailable' — the engine could not be loaded
 *                       'empty-output'   — FFmpeg returned nothing usable
 *     a cancelled signal rejects with an AbortError (window.__isUserCancel).
 *   WebmToMp4.fits(blob) → false when the memory guard already refuses the file —
 *     the page does not offer a button that can only answer «too big» (30 d to
 *     30.10.2026: 44 such refusals in 5 sessions, one person clicked 20 times).
 *
 * ultrafast: screen and webcam footage is flat and mostly still; the preset
 * costs a few percent of size and halves the wait on one WASM thread.
 */
(function () {
    'use strict';
    if (window.WebmToMp4) return;

    function typed(code, msg) { var e = new Error(msg || code); e.code = code; return e; }

    // The layout ships a stub FFmpegProcessor; the real module is pulled by
    // window.__ensureFFmpeg() (same door the other lanes use). A page that
    // already loads ffmpeg-processor.js by tag has the real one at once.
    async function engine() {
        var P = window.FFmpegProcessor;
        if ((!P || P.__isStub) && typeof window.__ensureFFmpeg === 'function') {
            try { await window.__ensureFFmpeg(); } catch (e) { throw typed('ffmpeg-unavailable', (e && e.message) || 'ffmpeg-load-failed'); }
            P = window.FFmpegProcessor;
        }
        if (!P || P.__isStub || typeof P.process !== 'function') throw typed('ffmpeg-unavailable');
        return P;
    }

    async function convert(blob, opts) {
        opts = opts || {};
        if (!blob) throw typed('empty-input');
        var name = opts.name || 'input.webm';
        var file = new File([blob], name, { type: blob.type || 'video/webm' });
        if (window.FFmpegMemoryGuard) {
            try { window.FFmpegMemoryGuard.assertSafe({ file: file, operation: 'video-transcode' }); }
            catch (g) { var tb = typed('too-big', (g && g.message) || 'too-big'); tb.cause = g; throw tb; }
        }
        if (opts.signal && opts.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        var P = await engine();
        if (typeof P.isReady === 'function' && !P.isReady()) {
            var ok = await P.init(function (p) { try { if (opts.onLoad) opts.onLoad(p || 0); } catch (_) {} });
            if (ok === false) throw typed('ffmpeg-unavailable', 'ffmpeg-init-failed');
        }
        var onFf = function (ev) {
            var p = ev && ev.detail;
            if (typeof p === 'number') { try { if (opts.onProgress) opts.onProgress(p); } catch (_) {} }
        };
        window.addEventListener('ffmpeg-progress', onFf);
        var onAbort = null;
        if (opts.signal && typeof P.cancel === 'function') {
            onAbort = function () { try { P.cancel(); } catch (_) {} };
            opts.signal.addEventListener('abort', onAbort, { once: true });
        }
        try {
            var args = ['-c:v', 'libx264', '-preset', 'ultrafast', '-crf', String(opts.crf || 23), '-pix_fmt', 'yuv420p',
                '-c:a', 'aac', '-b:a', '128k', '-movflags', '+faststart'];
            var r = await P.process(file, args, 'output.mp4');
            var out = r && r.blob;
            if (!out || out.size < 1024) throw typed('empty-output');
            return out.type === 'video/mp4' ? out : new Blob([out], { type: 'video/mp4' });
        } finally {
            window.removeEventListener('ffmpeg-progress', onFf);
            if (onAbort && opts.signal) opts.signal.removeEventListener('abort', onAbort);
        }
    }

    function fits(blob) {
        if (!blob || !window.FFmpegMemoryGuard) return true;    // nothing to ask: offer, convert() asks again
        try { window.FFmpegMemoryGuard.assertSafe({ file: new File([blob], 'input.webm', { type: blob.type || 'video/webm' }), operation: 'video-transcode' }); return true; }
        catch (_) { return false; }
    }

    window.WebmToMp4 = { convert: convert, fits: fits };
})();
